import baseQuery from "./baseQuery";
import { createApi } from "@reduxjs/toolkit/query/react";

export const compassPortalApi = createApi({
    reducerPath: 'compassPortalApi',
    baseQuery: baseQuery,
    tagTypes: ['PortalProjects', 'PortalGroups', 'PortalSources', 'PortalComments', 'PortalSourceComments', 'PortalFileVersions'],
    endpoints: (builder) => ({

        // ── Clients ───────────────────────────────────────

        getAccessibleClients: builder.query({
            query: () => `compass_portal/clients`,
        }),

        // ── Projects ─────────────────────────────────────

        getMyPortalProjects: builder.query({
            query: () => `compass_portal/projects/my_projects`,
            providesTags: ['PortalProjects'],
        }),
        getPortalProjects: builder.query({
            query: ({ clientName }) => `compass_portal/projects/by_client/${clientName}`,
            providesTags: ['PortalProjects'],
        }),
        getPortalProject: builder.query({
            query: ({ projectId }) => `compass_portal/projects/${projectId}`,
            providesTags: ['PortalProjects'],
        }),
        createPortalProject: builder.mutation({
            query: (body) => ({
                url: 'compass_portal/projects',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['PortalProjects'],
        }),
        updatePortalProject: builder.mutation({
            query: ({ projectId, ...body }) => ({
                url: `compass_portal/projects/${projectId}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['PortalProjects'],
        }),
        deletePortalProject: builder.mutation({
            query: ({ projectId }) => ({
                url: `compass_portal/projects/${projectId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['PortalProjects'],
        }),
        updateStageStatus: builder.mutation({
            query: ({ projectId, ...body }) => ({
                url: `compass_portal/projects/${projectId}/stage_status`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['PortalProjects'],
        }),

        // ── Data Groups ──────────────────────────────────

        getDataGroups: builder.query({
            query: ({ projectId }) => `compass_portal/projects/${projectId}/groups`,
            providesTags: ['PortalGroups'],
        }),
        createDataGroup: builder.mutation({
            query: ({ projectId, ...body }) => ({
                url: `compass_portal/projects/${projectId}/groups`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['PortalGroups'],
        }),
        updateDataGroup: builder.mutation({
            query: ({ projectId, groupId, ...body }) => ({
                url: `compass_portal/projects/${projectId}/groups/${groupId}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['PortalGroups'],
        }),
        deleteDataGroup: builder.mutation({
            query: ({ projectId, groupId }) => ({
                url: `compass_portal/projects/${projectId}/groups/${groupId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['PortalGroups', 'PortalSources'],
        }),
        signOffGroup: builder.mutation({
            query: ({ projectId, groupId, signOff = true }) => ({
                url: `compass_portal/projects/${projectId}/groups/${groupId}/sign_off?sign_off=${signOff}`,
                method: 'PUT',
            }),
            invalidatesTags: ['PortalGroups'],
        }),

        // ── Data Sources ─────────────────────────────────

        getDataSources: builder.query({
            query: ({ projectId }) => `compass_portal/projects/${projectId}/sources`,
            providesTags: ['PortalSources'],
        }),
        createDataSource: builder.mutation({
            query: ({ projectId, ...body }) => ({
                url: `compass_portal/projects/${projectId}/sources`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['PortalSources'],
        }),
        updateDataSource: builder.mutation({
            query: ({ projectId, sourceId, ...body }) => ({
                url: `compass_portal/projects/${projectId}/sources/${sourceId}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['PortalSources'],
        }),
        deleteDataSource: builder.mutation({
            query: ({ projectId, sourceId }) => ({
                url: `compass_portal/projects/${projectId}/sources/${sourceId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['PortalSources'],
        }),
        toggleSourceField: builder.mutation({
            query: ({ projectId, sourceId, field }) => ({
                url: `compass_portal/projects/${projectId}/sources/${sourceId}/toggle`,
                method: 'PUT',
                body: { field },
            }),
            invalidatesTags: ['PortalSources'],
        }),
        updateProcessingIssue: builder.mutation({
            query: ({ projectId, sourceId, ...body }) => ({
                url: `compass_portal/projects/${projectId}/sources/${sourceId}/processing_issue`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['PortalSources'],
        }),

        // ── File Inspection / Upload / Download ──────────

        inspectFile: builder.mutation({
            query: ({ projectId, file }) => {
                const formData = new FormData();
                formData.append('file', file);
                return {
                    url: `compass_portal/projects/${projectId}/inspect_file`,
                    method: 'POST',
                    body: formData,
                };
            },
        }),
        uploadPortalFile: builder.mutation({
            query: ({ projectId, sourceId, file }) => {
                const formData = new FormData();
                formData.append('source_id', sourceId);
                formData.append('file', file);
                return {
                    url: `compass_portal/projects/${projectId}/upload_file`,
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: ['PortalSources', 'PortalFileVersions'],
        }),
        getFileDownloadUrl: builder.query({
            query: ({ projectId, sourceId }) =>
                `compass_portal/projects/${projectId}/sources/${sourceId}/download`,
        }),

        // ── Comments ─────────────────────────────────────

        getComments: builder.query({
            query: ({ projectId, groupId }) =>
                `compass_portal/projects/${projectId}/groups/${groupId}/comments`,
            providesTags: ['PortalComments'],
        }),
        addComment: builder.mutation({
            query: ({ projectId, groupId, text }) => ({
                url: `compass_portal/projects/${projectId}/groups/${groupId}/comments`,
                method: 'POST',
                body: { text },
            }),
            invalidatesTags: ['PortalComments'],
        }),
        deleteComment: builder.mutation({
            query: ({ projectId, groupId, commentId }) => ({
                url: `compass_portal/projects/${projectId}/groups/${groupId}/comments/${commentId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['PortalComments'],
        }),
        addReply: builder.mutation({
            query: ({ projectId, groupId, commentId, text }) => ({
                url: `compass_portal/projects/${projectId}/groups/${groupId}/comments/${commentId}/replies`,
                method: 'POST',
                body: { text },
            }),
            invalidatesTags: ['PortalComments'],
        }),
        deleteReply: builder.mutation({
            query: ({ projectId, groupId, commentId, replyId }) => ({
                url: `compass_portal/projects/${projectId}/groups/${groupId}/comments/${commentId}/replies/${replyId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['PortalComments'],
        }),

        // ── File Versions ────────────────────────────────

        getFileVersions: builder.query({
            query: ({ projectId, sourceId }) =>
                `compass_portal/projects/${projectId}/sources/${sourceId}/file_versions`,
            providesTags: ['PortalFileVersions'],
        }),

        // ── Processed Files ─────────────────────────────

        uploadProcessedFile: builder.mutation({
            query: ({ projectId, sourceId, file }) => {
                const formData = new FormData();
                formData.append('file', file);
                return {
                    url: `compass_portal/projects/${projectId}/sources/${sourceId}/upload_processed`,
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: ['PortalSources', 'PortalFileVersions'],
        }),

        // ── Source Comments ─────────────────────────────

        getSourceComments: builder.query({
            query: ({ projectId, sourceId }) =>
                `compass_portal/projects/${projectId}/sources/${sourceId}/comments`,
            providesTags: ['PortalSourceComments'],
        }),
        addSourceComment: builder.mutation({
            query: ({ projectId, sourceId, text }) => ({
                url: `compass_portal/projects/${projectId}/sources/${sourceId}/comments`,
                method: 'POST',
                body: { text },
            }),
            invalidatesTags: ['PortalSourceComments'],
        }),
        deleteSourceComment: builder.mutation({
            query: ({ projectId, sourceId, commentId }) => ({
                url: `compass_portal/projects/${projectId}/sources/${sourceId}/comments/${commentId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['PortalSourceComments'],
        }),
        addSourceReply: builder.mutation({
            query: ({ projectId, sourceId, commentId, text }) => ({
                url: `compass_portal/projects/${projectId}/sources/${sourceId}/comments/${commentId}/replies`,
                method: 'POST',
                body: { text },
            }),
            invalidatesTags: ['PortalSourceComments'],
        }),
        deleteSourceReply: builder.mutation({
            query: ({ projectId, sourceId, commentId, replyId }) => ({
                url: `compass_portal/projects/${projectId}/sources/${sourceId}/comments/${commentId}/replies/${replyId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['PortalSourceComments'],
        }),
    }),
});

export const {
    // Clients
    useGetAccessibleClientsQuery,
    // Projects
    useGetMyPortalProjectsQuery,
    useGetPortalProjectsQuery,
    useGetPortalProjectQuery,
    useCreatePortalProjectMutation,
    useUpdatePortalProjectMutation,
    useDeletePortalProjectMutation,
    useUpdateStageStatusMutation,
    // Groups
    useGetDataGroupsQuery,
    useCreateDataGroupMutation,
    useUpdateDataGroupMutation,
    useDeleteDataGroupMutation,
    useSignOffGroupMutation,
    // Sources
    useGetDataSourcesQuery,
    useCreateDataSourceMutation,
    useUpdateDataSourceMutation,
    useDeleteDataSourceMutation,
    useToggleSourceFieldMutation,
    useUpdateProcessingIssueMutation,
    // Files
    useInspectFileMutation,
    useUploadPortalFileMutation,
    useGetFileDownloadUrlQuery,
    useLazyGetFileDownloadUrlQuery,
    // Comments (group-level)
    useGetCommentsQuery,
    useAddCommentMutation,
    useDeleteCommentMutation,
    useAddReplyMutation,
    useDeleteReplyMutation,
    // File versions
    useGetFileVersionsQuery,
    // Processed files
    useUploadProcessedFileMutation,
    // Source comments
    useGetSourceCommentsQuery,
    useAddSourceCommentMutation,
    useDeleteSourceCommentMutation,
    useAddSourceReplyMutation,
    useDeleteSourceReplyMutation,
} = compassPortalApi;

export default compassPortalApi.reducer;
